import React, { useEffect, useRef, useState } from "react";
import img1 from "../../images/alliances-1.jpg";
import img2 from "../../images/alliances-2.jpg";

/* ---------- Hook: in-view trigger (animate when section becomes visible) ---------- */
function useInView({ threshold = 0.25, rootMargin = "0px 0px -15% 0px" } = {}) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), {
      threshold,
      rootMargin,
    });

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, active };
}

function AlliancesAndCoalitions() {
  const { ref, active } = useInView();

  return (
    <>
      <style>{`
        /* Force list left aligned */
        .section-title-body ul,
        .section-title-body ol,
        .section-title-body li {
          text-align: left !important;
        }

        .alli-grid {
          margin-top: 28px;
        }

        /* Text entrance */
        .alli-text {
          opacity: 0;
          transform: translateY(14px);
          filter: blur(10px);
          transition: opacity 700ms ease, transform 700ms cubic-bezier(.2,.9,.2,1), filter 700ms ease;
        }
        .alli-wrap.is-active .alli-text {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }

        /* Small “partner chips” (kept as text-only) */
        .alli-chips {
          margin-top: 14px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .alli-chip {
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.16);
          backdrop-filter: blur(10px);
          font-size: 12px;
          letter-spacing: 0.02em;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 650ms ease, transform 650ms ease;
        }
        .alli-wrap.is-active .alli-chip {
          opacity: 1;
          transform: translateY(0);
        }
        .alli-wrap.is-active .alli-chip:nth-child(1) { transition-delay: 120ms; }
        .alli-wrap.is-active .alli-chip:nth-child(2) { transition-delay: 170ms; }
        .alli-wrap.is-active .alli-chip:nth-child(3) { transition-delay: 220ms; }
        .alli-wrap.is-active .alli-chip:nth-child(4) { transition-delay: 270ms; }
        .alli-wrap.is-active .alli-chip:nth-child(5) { transition-delay: 320ms; }
        .alli-wrap.is-active .alli-chip:nth-child(6) { transition-delay: 370ms; }
        .alli-large-img{
          width: 100%;
          max-width: 850px;
          height: auto;
          border-radius: 20px;
        }
        /* ===== ULTRA PREMIUM ALLIANCE IMAGE FRAME ===== */

.alli-image-frame{
  position:relative;
  display:inline-block;
  padding:10px;
  border-radius:34px;
  overflow:hidden;
  max-width: 900px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.18),
      rgba(255,255,255,0.04)
    );

  backdrop-filter: blur(18px);

  box-shadow:
    0 20px 60px rgba(0,0,0,0.45),
    inset 0 1px 1px rgba(255,255,255,0.25),
    inset 0 -1px 1px rgba(255,255,255,0.08);

  transition:
    transform .6s ease,
    box-shadow .6s ease;
}

/* Rotating luxury border */
.alli-image-frame::before{
  content:"";
  position:absolute;
  width:220%;
  height:220%;
  top:-60%;
  left:-60%;

  background:
    conic-gradient(
      from 0deg,
      #ffcc00,
      #ffffff,
      #ff7b00,
      #ffd700,
      #fff6b0,
      #ffcc00
    );

  animation: rotateFrame 7s linear infinite;
}

/* Inner dark glass layer */
.alli-image-frame::after{
  content:"";
  position:absolute;
  inset:4px;
  border-radius:30px;

  background:
    linear-gradient(
      145deg,
      rgba(20,20,20,0.20),
      rgba(255,255,255,0.04)
    );

  z-index:1;
}

/* Image Styling */
.alli-large-img{
  position:relative;
  z-index:2;

  width:100%;
  max-width: 1100px;
  height:auto;

  border-radius:24px;

  border:2px solid rgba(255,255,255,0.15);

  box-shadow:
    0 10px 30px rgba(0,0,0,0.35);

  transition:
    transform .6s ease,
    filter .6s ease;
}

/* Floating glow */
.alli-image-frame:hover{
  transform:translateY(-10px) scale(1.015);

  box-shadow:
    0 25px 80px rgba(0,0,0,0.6),
    0 0 40px rgba(255,215,0,0.35),
    0 0 90px rgba(255,140,0,0.20);
}

.alli-image-frame:hover .alli-large-img{
  transform:scale(1.03);
  filter:brightness(1.06);
}

/* Elegant top shine */
.alli-shine{
  position:absolute;
  top:0;
  left:-120%;
  width:50%;
  height:100%;

  background:linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,0.35),
    transparent
  );

  transform:skewX(-25deg);
  z-index:3;

  animation: shineMove 5s infinite;
}
/* Remove line/divider under heading */
.hra-topTitle::before,
.hra-topTitle::after,
.head-title::before,
.head-title::after {
  display: none !important;
  content: none !important;
  border: none !important;
}
.section-divider{
  width:100%;
  max-width:900px;
  margin:32px auto 38px;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:18px;
}

.section-divider::before,
.section-divider::after{
  content:"";
  flex:1;
  height:3px;

  background: linear-gradient(
    to right,
    transparent,
    #1a2a5a,
    #d4af37,
    transparent
  );

  border-radius:10px;

  box-shadow:
    0 2px 6px rgba(0,0,0,0.18);
}

.section-divider span{
  font-size:26px;
  color:#1a2a5a;
  text-shadow:0 2px 6px rgba(0,0,0,0.15);

  animation: dividerGlow 2.5s ease-in-out infinite;
}
.hra-topTitle{
  margin-bottom: -32px !important;
}
/* Animations */
@keyframes rotateFrame{
  100%{
    transform:rotate(360deg);
  }
}

@keyframes shineMove{
  0%{
    left:-120%;
  }
  50%{
    left:140%;
  }
  100%{
    left:140%;
  }
}

/* Mobile */
@media(max-width:768px){
  .alli-image-frame{
    padding:12px;
    border-radius:24px;
  }

  .alli-large-img{
    border-radius:18px;
  }

  /* Keep heading fully below fixed navbar */
  .hra-topTitle{
    margin-top: 8px !important;
    margin-bottom: 8px !important;
    padding: 0 8px;
    text-align: center;
  }

  .hra-topTitle .uppercase{
    font-size: 1.35rem !important;
    line-height: 1.25 !important;
    margin-top: 0 !important;
  }

  .hra-topTitle .lowercase{
    font-size: 0.9rem !important;
    line-height: 1.3 !important;
    margin-top: 6px !important;
  }

  .section-divider{
    margin: 14px auto 18px !important;
    gap: 10px !important;
  }

  .section-divider span{
    font-size: 16px !important;
  }

  /* Intro text */
  .hra-topIntro{
    font-size: 14px !important;
    line-height: 1.5 !important;
    margin-bottom: 12px !important;
  }

  /* All paragraphs */
  .alli-wrap p{
    font-size: 14px !important;
    line-height: 1.6 !important;
    margin-bottom: 12px !important;
    padding: 0 4px;
  }

  /* Bullet lists */
  .alli-wrap ul li{
    font-size: 14px !important;
    line-height: 1.5 !important;
    margin-bottom: 6px !important;
  }

  /* Reduce gaps */
  .alli-grid{
    margin-top: 10px !important;
  }

  .mt-4{
    margin-top: 12px !important;
  }

  .mt-5{
    margin-top: 18px !important;
  }

  .mb-4{
    margin-bottom: 14px !important;
  }

  /* Images */
  .alli-image-frame{
    padding: 8px !important;
    border-radius: 18px !important;
  }

  .alli-large-img{
    border-radius: 12px !important;
  }

  /* Divider */
  .section-divider{
    margin: 12px auto 8px !important;
  }

  .section-divider span{
    font-size: 18px !important;
  }
}
        /* Reduced motion safety */
        @media (prefers-reduced-motion: reduce) {
          .alli-text,
          .alli-chip {
            transition: none !important;
            animation: none !important;
            filter: none !important;
            transform: none !important;
          }
        }
      `}</style>

      <section>
        <div className="block color-scheme-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="section-sub-title center">
                  <article ref={ref} className={`section-title-body white alli-wrap ${active ? "is-active" : ""}`}>
                    <h1 className="head-title hra-topTitle">
                      <span
                        className="uppercase"
                        style={{ display: "block" }}
                      >
                        Alliances & Coalitions
                      </span>

                      <span
                        className="lowercase"
                        style={{ display: "block", marginTop: "6px" }}
                      >
                        (Interfaith, Ethnicities, Nations)
                      </span>
                                          <div className="section-divider">
                      <span>✦ ✦ ✦</span>
                    </div> 
                    </h1>

                    <p className="hra-topIntro">Strategic partnerships include:</p>

                    <p style={{textAlign:"left"}}>Americans4Hindus actively cultivates strategic <b>alliances and coalitions</b> that strengthen civic engagement, broaden representation, and promote shared democratic values across diverse communities. Through these partnerships, the organization builds bridges between Hindu Americans and other faith, cultural, and civic groups creating unified platforms for dialogue, advocacy, and collective action on issues of mutual concern, including religious freedom, pluralism, civil rights, and inclusive public policy.</p>

                    <div className="row align-items-start alli-grid g-4">
                      {/* ONLY CONTENT (no images) */}
                      <div className="col-12">
                        <div className="alli-text">
                          <div className="mt-4">
                            <ul className="arrow-list">
                              <li>Hindu–Jewish Alliance</li>
                              <li>Hindu–Christian Bridge Network</li>
                              <li>Hindu–Latino Partnerships</li>
                              
                            </ul>
                              <div className="row mt-5 justify-content-center">
                                <div className="col-12 mb-4 mx-auto text-center">
                                  <div className="alli-image-frame">
                                    <span className="alli-shine"></span>

                                    <span className="corner-accent top-left"></span>
                                    <span className="corner-accent bottom-right"></span>

                                    <img
                                      src={img1}
                                      alt="Alliance collaboration"
                                      className="img-fluid alli-large-img"
                                    />
                                  </div>
                              </div>
                              <p style={{textAlign:"left"}}> By fostering collaborative networks such as interfaith coalitions and multi-community engagement forums, Americans4Hindus amplifies voices that champion equality, tolerance, and respect within the political sphere, while advancing greater understanding and cooperation among constituencies that reflect the rich diversity of American society. These alliances not only enhance the effectiveness of advocacy efforts but also support broader initiatives that educate, mobilize, and empower voters and future leaders across generations.</p>
                              <ul className="arrow-list">
                                <li>National student alliances</li>
                                <li>Business coalitions</li>
                                <li>Multicultural civic partnerships</li>
                              </ul>
                              <div className="col-12 mb-4 mx-auto text-center">
                                <div className="alli-image-frame">
                                  <span className="alli-shine"></span>

                                  <span className="corner-accent top-left"></span>
                                  <span className="corner-accent bottom-right"></span>

                                  <img
                                    src={img2}
                                    alt="Coalition partnership"
                                    className="img-fluid alli-large-img"
                                  />
                                </div>
                              </div>
                            </div>

                          </div>

                        </div>
                      </div>
                    </div>

                  </article>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default AlliancesAndCoalitions;
